import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { api } from '@shared/api/client';
import { asRows } from '@shared/api/envelope';
import { key, STALE_TIME } from '@shared/api/query-keys';

import { fromWire, type WireBody } from './body';
import type { StateFilter } from './states';

import type {
  ContentPermissions,
  EditorialPostDetail,
  EditorialPostListPayload,
  RelatedTitle,
  PostTag,
} from './types';

/**
 * Every read the four editorial surfaces perform.
 *
 * **The review queue is not a query.** It is this list filtered to
 * `AWAITING_REVIEW` on the client, because the queue *is* a view of a list the
 * console already holds — a second fetch would put one post in two caches and
 * leave the chip counts disagreeing with the table the moment a state moved.
 * `GET /api/v1/admin/editorialReviewQueue` exists on the owning service and is
 * deliberately not called from here for that reason.
 */

/**
 * The page is part of the key, so each one caches on its own. A transition
 * still invalidates the whole resource — `mutations.ts` invalidates
 * `['content', 'editorialPosts']`, which is a prefix of every page's key.
 */
export function postsKey(page: number, term = '', state: StateFilter = 'all') {
  return key('content', 'editorialPosts', { page, term, state });
}

export function postKey(postId: string) {
  return key('content', 'editorialPost', postId);
}

export function permissionsKey() {
  return key('content', 'permissions');
}

export function tagsKey() {
  return key('content', 'tags');
}

export function relatedCandidatesKey(term: string) {
  return key('content', 'adminItems', { for: 'editorialRelated', term });
}

/**
 * One page of the list — **narrowed on the service**, in every dimension.
 *
 * `q` and `state` are wire parameters now, and that changes what the console's
 * own controls mean. The facet and its counts used to be a view over the twenty
 * rows the page held: a chip read «٣» over a backlog of thirty, and the review
 * queue — which *is* this list filtered to `AWAITING_REVIEW` — was the
 * awaiting-review posts that happened to land on the page being read. The
 * service now compiles both narrowings into the query it pages, so a page of
 * «بانتظار المراجعة» is the first page of that state rather than the members of
 * it that survived page one of everything.
 *
 * `state_counts` comes back on the same response, counted under the term alone,
 * so each chip prints what pressing it would show and no second request can
 * disagree with the table beside it.
 *
 * **No `sort` is sent.** The date order the table offers is a reading order the
 * operator chooses over the page in hand; it writes nothing, and the service
 * declares no sort parameter to carry it.
 */
export function usePosts(page: number, term = '', state: StateFilter = 'all') {
  const trimmed = term.trim();

  return useQuery({
    queryKey: postsKey(page, trimmed, state),
    queryFn: () => {
      const params = new URLSearchParams({ current_page: String(page) });
      if (trimmed) params.set('q', trimmed);
      if (state !== 'all') params.set('state', state);
      return api.get<EditorialPostListPayload>('content', `/admin/editorialPosts?${params.toString()}`);
    },
    staleTime: STALE_TIME.list,
    // Walking a page — or typing another letter — keeps the rows on screen while
    // the next set loads, so the table does not blink through a skeleton
    // between keystrokes.
    placeholderData: keepPreviousData,
  });
}

/**
 * One post, body included.
 *
 * **`GET /api/v1/admin/editorialPosts/{postId}` is not declared by the owning
 * service** — its administration table names the list, the create, the patch
 * and the transitions, and no per-id read. It is called here because the two
 * routes that need a body cannot be served by a list row, and because this
 * repo's own route map already assumes it. The gap is named in this run's
 * report; it is one row on the owning side, not a shape this console can work
 * around.
 */
export function usePost(postId: string | undefined) {
  return useQuery({
    queryKey: postKey(postId ?? ''),
    queryFn: async () => {
      const payload = await api.get<EditorialPostDetail & WireBody>(
        'content',
        `/admin/editorialPosts/${postId}`,
      );

      // **The body is resolved HERE, at the read, not in each surface.** The
      // service stores `body_html`; the editor and the renderer hold a block
      // tree. Both routes used to take `payload.body` straight off the wire,
      // which is a key the service does not serve — so the canvas would open
      // EMPTY over a post that has prose, and the next save would write that
      // emptiness over the writer's text. `fromWire` prefers a tree where one is
      // present and parses the markup otherwise.
      return { ...payload, body: fromWire(payload) };
    },
    // A decision must reflect backend state immediately: the reviewer is about
    // to publish what this read returned.
    staleTime: STALE_TIME.detail,
    enabled: Boolean(postId),
  });
}

/**
 * Whether this caller holds the granted reviewer role.
 *
 * Lazy per section and cached for the session, and it gates **row-level CTAs
 * only** — never a read, and never an authorization outcome. The owning service
 * refuses the two decisions on its own side regardless of what renders here.
 */
export function usePermissions() {
  return useQuery({
    queryKey: permissionsKey(),
    queryFn: () => api.get<ContentPermissions>('content', '/me/permissions'),
    staleTime: STALE_TIME.vocabulary,
  });
}

/** The tag vocabulary the tag picker draws from. Picked, never typed. */
export function useTags() {
  return useQuery({
    queryKey: tagsKey(),
    // A bare array on the wire — the vocabulary carries no pagination.
    queryFn: async () => ({ items: asRows<PostTag>(await api.get('content', '/tags')) }),
    staleTime: STALE_TIME.vocabulary,
  });
}

/**
 * Candidate titles for the related-titles picker — **searched on the server**.
 *
 * `GET /admin/contentItems` now takes `q`, and this is where that matters. The
 * picker used to read one unparameterised page and filter the 20 rows it came
 * back with, so a title past the first page could not be attached and, worse,
 * the surface said «لا عنوان بهذا الاسم» — a claim that the title does not
 * exist, for a title that plainly does. An operator reads that as an answer, not
 * as a ceiling.
 *
 * `page_size` is the route's own bound quoted back rather than a number this
 * console picked. Titles are offered across every publish state, because a post
 * may recommend a coming-soon one.
 */
export function useRelatedCandidates(term: string, enabled: boolean) {
  const trimmed = term.trim();

  return useQuery({
    queryKey: relatedCandidatesKey(trimmed),
    queryFn: () => {
      const query = new URLSearchParams({ page_size: String(MAX_PAGE_SIZE) });
      if (trimmed) query.set('q', trimmed);
      return api.get<{ items: RelatedTitle[] }>('content', `/admin/contentItems?${query.toString()}`);
    },
    staleTime: STALE_TIME.list,
    // The previous term's rows stay on screen while the next read is in flight,
    // so the list does not blank out between keystrokes.
    placeholderData: (previous) => previous,
    enabled,
  });
}

/**
 * The service's own ceiling on a page (`Query(ge=1, le=100)`), quoted back.
 *
 * A number invented here would put a server-owned limit behind a rebuild of this
 * bundle.
 */
const MAX_PAGE_SIZE = 100;

/** The subject type an editorial post's artwork is registered under. */
const ARTWORK_SUBJECT_TYPE = 'EDITORIAL_POST';

/**
 * The one purpose an operator uploads for. The hero is `derived` — `assets`
 * crops it out of the thumbnail — so the thumbnail's floor is the only floor a
 * chosen file is measured against.
 */
const ARTWORK_PURPOSE = 'thumbnail';

export function artworkPolicyKey() {
  return key('assets', 'uploadPolicy', ARTWORK_SUBJECT_TYPE);
}

interface WireArtworkPolicy {
  acceptedArtworkFormats?: string[];
  maxArtworkUploadMb?: number;
  artwork?: Record<string, { purposes?: Record<string, string>; ratios?: Record<string, string> }>;
  minDimensionsByRatio?: Record<string, number[]>;
}

/** The four numbers the artwork controls state, every one of them read. */
export interface ArtworkPolicy {
  accepted_formats: string[];
  max_size_bytes: number | null;
  min_width: number | null;
  min_height: number | null;
}

/**
 * The upload contract for an editorial post's artwork, **narrowed to the
 * subject**, because the answer differs per subject type.
 *
 * **Every number the rail prints comes from here rather than from a literal
 * beside it.** The hints used to read «المقاس الموصى به 1280×720» and «2400×1000»,
 * written down in `copy.ts`. Neither number was the gate: `assets` refuses a
 * 16:9 source below `ARTWORK_PURPOSE_MIN_DIMENSIONS['16:9']`, seeded at
 * 1920×1080, so an operator who followed the console's own guidance to the pixel
 * met `ASSETS_ARTWORK_TOO_SMALL`. The formats, the cap and the floor are all
 * operator-owned configuration rows; a literal here is wrong the day one of them
 * changes, and wrong silently.
 *
 * This is the same read `usePortraitPolicy` performs for the credited-person
 * portrait, against the same route with a different `subjectType`.
 */
export function useArtworkPolicy() {
  return useQuery({
    queryKey: artworkPolicyKey(),
    queryFn: async (): Promise<ArtworkPolicy> => {
      const wire = await api.get<WireArtworkPolicy>(
        'assets',
        `/uploadPolicy?subjectType=${ARTWORK_SUBJECT_TYPE}`,
      );
      const ratio = wire.artwork?.[ARTWORK_SUBJECT_TYPE]?.ratios?.[ARTWORK_PURPOSE] ?? '';
      const floor = wire.minDimensionsByRatio?.[ratio];

      return {
        accepted_formats: wire.acceptedArtworkFormats ?? [],
        max_size_bytes:
          typeof wire.maxArtworkUploadMb === 'number' ? wire.maxArtworkUploadMb * BYTES_PER_MB : null,
        min_width: floor?.[0] ?? null,
        min_height: floor?.[1] ?? null,
      };
    },
    staleTime: STALE_TIME.vocabulary,
  });
}

const BYTES_PER_MB = 1_048_576;
